// src/hooks/api/useAdminTeams.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

// ─── Types ──────────────────────────────────────────────────────────────

export type Position = "GK" | "DEF" | "MID" | "FWD";

export interface AdminTeamUser {
  id: number;
  name: string;
  email: string;
  username: string | null;
  avatar: string | null;
}

export interface AdminTeamGameweek {
  id: number;
  name: string;
  status: "UPCOMING" | "OPEN" | "LOCKED" | "COMPLETED";
  phase: string;
  roundNumber: number;
}

export interface AdminTeamRow {
  id: number;
  gameweekId: number;
  gameweek: AdminTeamGameweek;
  userId: number;
  user: AdminTeamUser;
  captainId: number | null;
  totalPoints: number;
  isLocked: boolean;
  playerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminTeamPlayerCard {
  id: number;
  playerId: number;
  position: Position;
  isCaptain: boolean;
  points: number;
  rawPoints: number;
  player: {
    id: number;
    name: string;
    position: Position;
    photo: string | null;
    club: { id: number; name: string; logoUrl: string | null } | null;
    nation: {
      id: number;
      name: string;
      code: string;
      flagUrl: string | null;
    } | null;
  };
}

export interface AdminEligiblePlayer {
  id: number;
  name: string;
  position: Position;
  photo: string | null;
  club: { id: number; name: string; logoUrl: string | null } | null;
  nation: {
    id: number;
    name: string;
    code: string;
    flagUrl: string | null;
  } | null;
}

export interface AdminTeamDetail {
  team: AdminTeamRow;
  players: AdminTeamPlayerCard[];
  eligiblePlayers: AdminEligiblePlayer[];
}

export interface AdminTeamAudit {
  id: number;
  adminId: number;
  gameweekTeamId: number;
  gameweekId: number;
  action: "SWAP" | "CAPTAIN_CHANGE";
  outPlayerId: number | null;
  inPlayerId: number | null;
  newCaptainId: number | null;
  createdAt: string;
  admin: { id: number; name: string; email: string };
}

export interface AdminTeamFilters {
  gameweekId?: number;
  userId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

// ─── Query keys ─────────────────────────────────────────────────────────

const keys = {
  all: ["admin-teams"] as const,
  list: (filters?: AdminTeamFilters) =>
    [...keys.all, "list", filters] as const,
  detail: (id: number) => [...keys.all, "detail", id] as const,
  audit: (id: number) => [...keys.all, "audit", id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────

export function useAdminTeams(filters?: AdminTeamFilters) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: async (): Promise<{
      teams: AdminTeamRow[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }> => {
      const qs = new URLSearchParams();
      if (filters?.gameweekId) qs.set("gameweekId", String(filters.gameweekId));
      if (filters?.userId) qs.set("userId", String(filters.userId));
      if (filters?.search) qs.set("search", filters.search);
      if (filters?.page) qs.set("page", String(filters.page));
      if (filters?.limit) qs.set("limit", String(filters.limit));
      const q = qs.toString();
      return apiClient.get(
        `${API_ENDPOINTS.ADMIN_TEAMS.LIST}${q ? `?${q}` : ""}`,
      );
    },
  });
}

export function useAdminTeamDetail(id: number | null) {
  return useQuery({
    queryKey: id ? keys.detail(id) : [...keys.all, "detail", "none"],
    queryFn: async (): Promise<AdminTeamDetail> =>
      apiClient.get(API_ENDPOINTS.ADMIN_TEAMS.DETAIL(id!)),
    enabled: !!id,
  });
}

export function useAdminTeamAudit(id: number | null) {
  return useQuery({
    queryKey: id ? keys.audit(id) : [...keys.all, "audit", "none"],
    queryFn: async (): Promise<{ actions: AdminTeamAudit[] }> =>
      apiClient.get(API_ENDPOINTS.ADMIN_TEAMS.AUDIT(id!)),
    enabled: !!id,
  });
}

export function useAdminSwapPlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      outPlayerId,
      inPlayerId,
    }: {
      teamId: number;
      outPlayerId: number;
      inPlayerId: number;
    }) =>
      apiClient.post<AdminTeamDetail>(
        API_ENDPOINTS.ADMIN_TEAMS.SWAP(teamId),
        { outPlayerId, inPlayerId },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(vars.teamId) });
      qc.invalidateQueries({ queryKey: keys.audit(vars.teamId) });
    },
  });
}

export function useAdminChangeCaptain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      captainPlayerId,
    }: {
      teamId: number;
      captainPlayerId: number;
    }) =>
      apiClient.patch<AdminTeamDetail>(
        API_ENDPOINTS.ADMIN_TEAMS.CAPTAIN(teamId),
        { captainPlayerId },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(vars.teamId) });
      qc.invalidateQueries({ queryKey: keys.audit(vars.teamId) });
    },
  });
}
